import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";

export default function Signup() {
  const [form, setForm] = useState({ full_name: "", company: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { signup } = useAuth();
  const nav = useNavigate();

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      // Capture lead-attribution source: ?utm_source=..., ?ref=..., or document.referrer
      const urlParams = new URLSearchParams(window.location.search);
      let source =
        urlParams.get("utm_source") ||
        urlParams.get("ref") ||
        urlParams.get("source");
      if (!source) {
        const ref = document.referrer || "";
        if (!ref) source = "direct";
        else if (ref.includes(window.location.host)) source = "landing";
        else if (/google\./.test(ref)) source = "google";
        else if (/bing\./.test(ref)) source = "bing";
        else if (/twitter\.|x\.com/.test(ref)) source = "twitter";
        else if (/linkedin\./.test(ref)) source = "linkedin";
        else if (/facebook\./.test(ref)) source = "facebook";
        else source = "referral";
      }
      await signup({ ...form, source });
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <SEO title="Create Account — Free 7-Day Trial" description="Launch your commercial real-estate portfolio workspace in under 90 seconds. Free 7-day trial. Cancel anytime." path="/signup" noindex />
      <div className="hidden lg:flex bg-marketing grid-texture text-white p-12 flex-col justify-between">
        <Link to="/" className="flex items-center gap-2 text-white" data-testid="signup-back-home">
          <ArrowLeft className="w-4 h-4" /> <span className="font-semibold">Back to site</span>
        </Link>
        <div>
          <div className="overline text-white/40 mb-4">Free 7-day trial</div>
          <h2 className="text-4xl font-bold tracking-tighter max-w-md" style={{fontFamily:"Outfit"}}>
            Spin up your CRE workspace in <span className="italic font-light text-white/50">under 90 seconds.</span>
          </h2>
          <ul className="mt-8 space-y-2 text-sm text-white/70">
            <li>— Unlimited tenants on every plan</li>
            <li>— Stripe-secured rent collection</li>
            <li>— Cancel anytime, no lock-in</li>
          </ul>
        </div>
        <div className="text-xs text-white/30">© 2026 RealtyVault Manager</div>
      </div>

      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          <div className="overline text-slate-500 mb-3">Create account</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"Outfit"}}>Launch your portfolio.</h1>
          <p className="mt-2 text-sm text-slate-500">Start free. Pick a plan once you're ready.</p>
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-slate-600">Full name</label>
                <input data-testid="signup-name-input" className="w-full mt-2 input-clean" value={form.full_name} onChange={handle("full_name")} required />
              </div>
              <div>
                <label className="overline text-slate-600">Company name <span className="lowercase tracking-normal text-slate-400 font-normal normal-case">(optional)</span></label>
                <input
                  data-testid="signup-company-input"
                  className="w-full mt-2 input-clean"
                  value={form.company}
                  onChange={handle("company")}
                  placeholder="e.g., Acme Realty Holdings, LLC"
                  maxLength={120}
                  autoComplete="organization"
                />
              </div>
            </div>
            <div>
              <label className="overline text-slate-600">Work email</label>
              <input type="email" data-testid="signup-email-input" className="w-full mt-2 input-clean" value={form.email} onChange={handle("email")} required />
            </div>
            <div>
              <label className="overline text-slate-600">Password</label>
              <input type="password" data-testid="signup-password-input" className="w-full mt-2 input-clean" value={form.password} onChange={handle("password")} required minLength={8} />
            </div>
            {err && <div className="text-sm text-red-600" data-testid="signup-error">{err}</div>}
            <button
              type="submit"
              disabled={busy}
              data-testid="signup-submit-button"
              className="w-full btn-primary-dark flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? "Creating…" : (<>Create account <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>
          <div className="mt-6 text-sm text-slate-500">
            Already have an account? <Link to="/login" className="text-slate-900 font-semibold hover:underline" data-testid="signup-go-login">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
